import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver/dist/FileSaver';

const ExportActions = ({ projections, onExport }) => {
  const [isExporting, setIsExporting] = useState(false);
  const [exportType, setExportType] = useState(null);
  const [shareSuccess, setShareSuccess] = useState(false);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })?.format(amount || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0)?.toFixed(1)}%`;
  };

  const handleExportPDF = async () => {
    try {
      // Create new PDF document
      const pdf = new jsPDF();
      const pageWidth = pdf?.internal?.pageSize?.getWidth();
      const pageHeight = pdf?.internal?.pageSize?.getHeight();
      let yPosition = 20;

      // Add Hookhub Logo
      try {
        const logoImg = new Image();
        logoImg.src = '/assets/images/Screenshot_2025-09-14_at_8.29.20_AM-1757810897942.png';
        
        // Create a promise to handle image loading
        await new Promise((resolve, reject) => {
          logoImg.onload = () => resolve();
          logoImg.onerror = () => reject();
        });

        // Calculate proper aspect ratio for logo
        const originalWidth = logoImg?.naturalWidth || logoImg?.width;
        const originalHeight = logoImg?.naturalHeight || logoImg?.height;
        const aspectRatio = originalWidth / originalHeight;
        
        // Set logo dimensions maintaining aspect ratio
        const logoWidth = 50; // Increased width for better visibility
        const logoHeight = logoWidth / aspectRatio; // Maintain proper aspect ratio
        
        pdf?.addImage(logoImg, 'PNG', 20, yPosition, logoWidth, logoHeight, undefined, 'FAST');
        
        // Add website URL next to logo
        pdf?.setFontSize(10);
        pdf?.setFont('helvetica', 'normal');
        pdf?.setTextColor(0, 102, 204); // Blue color for URL
        pdf?.text('www.hookhub.co', 20 + logoWidth + 10, yPosition + (logoHeight / 2));
        
        yPosition += Math.max(logoHeight, 20) + 10;
      } catch (logoError) {
        console.warn('Logo could not be loaded, continuing without logo:', logoError);
        // Add website URL without logo
        pdf?.setFontSize(12);
        pdf?.setFont('helvetica', 'bold');
        pdf?.setTextColor(0, 102, 204);
        pdf?.text('www.hookhub.co', 20, yPosition);
        yPosition += 15;
      }

      // Reset text color
      pdf?.setTextColor(0, 0, 0);

      // Header
      pdf?.setFontSize(20);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('RV Park Financial Projections', 20, yPosition);
      yPosition += 10;

      pdf?.setFontSize(12);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(`Generated on ${new Date()?.toLocaleDateString()}`, 20, yPosition);
      yPosition += 20;

      // Founder Message Section
      pdf?.setFontSize(14);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Message from Caylee Harrington, Founder of Hookhub', 20, yPosition);
      yPosition += 10;

      pdf?.setFontSize(11);
      pdf?.setFont('helvetica', 'normal');
      const founderMessage = [
        'At Hookhub, we\'re committed to empowering RV park investors with data-driven',
        'insights and comprehensive financial analysis. This projection tool represents',
        'our dedication to helping you make informed investment decisions in the',
        'growing RV hospitality industry.',
        '',
        'We believe in the power of strategic planning and transparent financial',
        'modeling to turn your RV park vision into a successful reality.',
        '',
        'For personalized consultation and investment guidance, please don\'t hesitate',
        'to reach out to our team.'
      ];

      founderMessage?.forEach((line) => {
        if (line === '') {
          yPosition += 5;
        } else {
          pdf?.text(line, 20, yPosition);
          yPosition += 5;
        }
      });

      yPosition += 10;

      // Contact Information
      pdf?.setFontSize(11);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Contact Information:', 20, yPosition);
      yPosition += 7;

      pdf?.setFont('helvetica', 'normal');
      pdf?.text('Email: hello@hookhub.co', 20, yPosition);
      pdf?.text('Website: www.hookhub.co', 20, yPosition + 7);
      yPosition += 20;

      // Summary Section
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('5-Year Financial Summary', 20, yPosition);
      yPosition += 15;

      // Calculate totals
      const totalRevenue = projections?.reduce((sum, p) => sum + (p?.grossRevenue || 0), 0);
      const totalCosts = projections?.reduce((sum, p) => sum + (p?.operatingCosts || 0), 0);
      const totalNetIncome = projections?.reduce((sum, p) => sum + (p?.netIncome || 0), 0);
      const avgGrowth = projections?.reduce((sum, p) => sum + (p?.growthRate || 0), 0) / (projections?.length || 1);

      pdf?.setFontSize(12);
      pdf?.setFont('helvetica', 'normal');
      pdf?.text(`Total 5-Year Revenue: ${formatCurrency(totalRevenue)}`, 20, yPosition);
      yPosition += 7;
      pdf?.text(`Total Operating Costs: ${formatCurrency(totalCosts)}`, 20, yPosition);
      yPosition += 7;
      pdf?.text(`Total Net Income: ${formatCurrency(totalNetIncome)}`, 20, yPosition);
      yPosition += 7;
      pdf?.text(`Average Growth Rate: ${formatPercentage(avgGrowth)}`, 20, yPosition);
      yPosition += 20;

      // Yearly Breakdown Table
      pdf?.setFontSize(16);
      pdf?.setFont('helvetica', 'bold');
      pdf?.text('Year-by-Year Breakdown', 20, yPosition);
      yPosition += 15;

      // Table headers
      pdf?.setFontSize(10);
      pdf?.setFont('helvetica', 'bold');
      const headers = ['Year', 'Gross Revenue', 'Operating Costs', 'Net Income', 'Growth Rate', 'ROI'];
      const colWidths = [20, 35, 35, 35, 25, 25];
      let xPos = 20;

      headers?.forEach((header, i) => {
        pdf?.text(header, xPos, yPosition);
        xPos += colWidths?.[i];
      });
      yPosition += 7;

      // Draw header underline
      pdf?.line(20, yPosition, pageWidth - 20, yPosition);
      yPosition += 5;

      // Table rows
      pdf?.setFont('helvetica', 'normal');
      projections?.forEach((proj) => {
        if (yPosition > pageHeight - 30) {
          pdf?.addPage();
          yPosition = 20;
        }

        xPos = 20;
        const rowData = [
          `Year ${proj?.year || 0}`,
          formatCurrency(proj?.grossRevenue),
          formatCurrency(proj?.operatingCosts),
          formatCurrency(proj?.netIncome),
          formatPercentage(proj?.growthRate),
          formatPercentage(proj?.roi)
        ];

        rowData?.forEach((data, i) => {
          pdf?.text(data, xPos, yPosition);
          xPos += colWidths?.[i];
        });
        yPosition += 7;
      });

      // Footer
      yPosition += 20;
      if (yPosition > pageHeight - 40) {
        pdf?.addPage();
        yPosition = 20;
      }
      
      pdf?.setFontSize(10);
      pdf?.setFont('helvetica', 'italic');
      pdf?.text('Note: These projections are estimates based on provided inputs and industry data.', 20, yPosition);
      pdf?.text('Actual results may vary. Consult with financial professionals for investment decisions.', 20, yPosition + 7);

      // Save the PDF
      pdf?.save('RV_Park_Financial_Projections.pdf');
      return true;

    } catch (error) {
      console.error('PDF export failed:', error);
      throw error;
    }
  };

  const handleExportExcel = async () => {
    try {
      // Create workbook
      const wb = XLSX?.utils?.book_new();

      // Summary data
      const summaryData = [
        ['RV Park Financial Projections Summary'],
        ['Generated on:', new Date()?.toLocaleDateString()],
        [''],
        ['5-Year Totals:'],
        ['Total Revenue:', projections?.reduce((sum, p) => sum + (p?.grossRevenue || 0), 0)],
        ['Total Costs:', projections?.reduce((sum, p) => sum + (p?.operatingCosts || 0), 0)],
        ['Total Net Income:', projections?.reduce((sum, p) => sum + (p?.netIncome || 0), 0)],
        ['Average Growth Rate:', `${(projections?.reduce((sum, p) => sum + (p?.growthRate || 0), 0) / (projections?.length || 1))?.toFixed(1)}%`],
        [''],
      ];

      // Projections data
      const projectionsData = [
        ['Year', 'Gross Revenue', 'Operating Costs', 'Net Income', 'Growth Rate (%)', 'ROI (%)'],
        ...(projections?.map(proj => [
          proj?.year || 0,
          proj?.grossRevenue || 0,
          proj?.operatingCosts || 0,
          proj?.netIncome || 0,
          Number((proj?.growthRate || 0)?.toFixed(1)),
          Number((proj?.roi || 0)?.toFixed(1))
        ]) || [])
      ];

      // Combine data
      const allData = [...summaryData, ...projectionsData];

      // Create worksheet
      const ws = XLSX?.utils?.aoa_to_sheet(allData);

      // Set column widths
      ws['!cols'] = [
        { wch: 20 }, // Year/Label column
        { wch: 15 }, // Revenue column
        { wch: 15 }, // Costs column
        { wch: 15 }, // Net Income column
        { wch: 15 }, // Growth Rate column
        { wch: 10 }  // ROI column
      ];

      // Add worksheet to workbook
      XLSX?.utils?.book_append_sheet(wb, ws, 'Financial Projections');

      // Generate Excel file buffer
      const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
      
      // Create blob and save
      const blob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      saveAs(blob, 'RV_Park_Financial_Projections.xlsx');
      
      return true;

    } catch (error) {
      console.error('Excel export failed:', error);
      throw error;
    }
  };

  const handleExport = async (type) => {
    setIsExporting(true);
    setExportType(type);
    
    try {
      let success = false;
      
      if (type === 'pdf') {
        success = await handleExportPDF();
      } else if (type === 'sheets') {
        success = await handleExportExcel();
      }
      
      if (success) {
        onExport?.(type);
      }
      
    } catch (error) {
      console.error('Export failed:', error);
      // You could add toast notification here for user feedback
      alert(`Export failed: ${error?.message}. Please try again.`);
    } finally {
      setIsExporting(false);
      setExportType(null);
    }
  };

  const handleShare = async () => {
    const url = window?.location?.href || document?.location?.href || '';
    
    try {
      // Check Web Share API availability with more thorough checks
      if (navigator?.share && 
          typeof navigator.share === 'function' && 
          window?.isSecureContext !== false &&
          'canShare' in navigator) {
        
        // Check if the content can be shared
        const shareData = {
          title: 'RV Park Financial Projections',
          text: 'Check out my RV park investment projections',
          url: url
        };
        
        // Use canShare to validate before attempting to share
        if (navigator.canShare && navigator.canShare(shareData)) {
          await navigator.share(shareData);
          return;
        }
      }
      
      // If Web Share API is not fully supported or permission denied, fallback to clipboard
      await copyToClipboard(url);
      
    } catch (error) {
      console.error('Share failed, falling back to clipboard:', error);
      // Always fallback to clipboard on any error
      await copyToClipboard(url);
    }
  };

  const copyToClipboard = async (url) => {
    try {
      // Modern clipboard API
      if (navigator?.clipboard && typeof navigator.clipboard?.writeText === 'function') {
        await navigator.clipboard?.writeText(url);
        setShareSuccess(true);
        setTimeout(() => setShareSuccess(false), 2000);
        return;
      }
      
      // Legacy fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body?.appendChild(textArea);
      textArea?.focus();
      textArea?.select();
      
      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setShareSuccess(true);
          setTimeout(() => setShareSuccess(false), 2000);
        }
      } catch (err) {
        console.error('Failed to copy URL using execCommand:', err);
        // Show error state or notification to user
      } finally {
        if (document.body?.contains(textArea)) {
          document.body?.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error('All clipboard methods failed:', error);
      // Could show a manual copy instruction to user
    }
  };

  return (
    <div className="flex flex-col sm:flex-row gap-3">
      <Button
        variant="outline"
        iconName="FileText"
        iconPosition="left"
        loading={isExporting && exportType === 'pdf'}
        disabled={isExporting || !projections?.length}
        onClick={() => handleExport('pdf')}
        className="flex-1 sm:flex-none"
      >
        {isExporting && exportType === 'pdf' ? 'Generating PDF...' : 'Export PDF'}
      </Button>
      <Button
        variant="outline"
        iconName="Sheet"
        iconPosition="left"
        loading={isExporting && exportType === 'sheets'}
        disabled={isExporting || !projections?.length}
        onClick={() => handleExport('sheets')}
        className="flex-1 sm:flex-none"
      >
        {isExporting && exportType === 'sheets' ? 'Creating Excel...' : 'Export to Excel'}
      </Button>
      <Button
        variant="ghost"
        iconName={shareSuccess ? "Check" : "Share2"}
        iconPosition="left"
        disabled={isExporting}
        onClick={handleShare}
        className="flex-1 sm:flex-none"
      >
        {shareSuccess ? 'Link Copied!' : 'Share'}
      </Button>
    </div>
  );
};

export default ExportActions;